import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  ReactNode,
} from "react";

export interface FilterPost {
  id: string;
  message: string;
  imageUrl: string | null;
  createdTime: string | null;
  permalinkUrl: string | null;
}

export interface PostFilterHandlers {
  onSelectPost: (postId: string) => void;
  onSelectGlobal: () => void;
  onSearchChange: (value: string) => void;
  onLoadMore: () => void;
}

export interface PostFilterState {
  isActive: boolean;
  posts: FilterPost[];
  selectedId: string | null; // null means "global" / All Posts
  searchQuery: string;
  loadingMore: boolean;
  hasMore: boolean;
  handlers: PostFilterHandlers | null;
}

const defaultState: PostFilterState = {
  isActive: false,
  posts: [],
  selectedId: null,
  searchQuery: "",
  loadingMore: false,
  hasMore: false,
  handlers: null,
};

interface PostFilterContextType {
  state: PostFilterState;
  setFilterState: (state: PostFilterState) => void;
  resetFilterState: () => void;
}

const PostFilterContext = createContext<PostFilterContextType | undefined>(
  undefined
);

export const usePostFilter = () => {
  const ctx = useContext(PostFilterContext);
  if (!ctx) {
    throw new Error("usePostFilter must be used within PostFilterProvider");
  }
  return ctx;
};

export const PostFilterProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<PostFilterState>(defaultState);

  const setFilterState = useCallback((next: PostFilterState) => {
    setState(next);
  }, []);

  const resetFilterState = useCallback(() => {
    setState(defaultState);
  }, []);

  return (
    <PostFilterContext.Provider
      value={{ state, setFilterState, resetFilterState }}
    >
      {children}
    </PostFilterContext.Provider>
  );
};
